/** Shared talk-vs-write router. Regex is the millisecond hard gate; LLM only confirms. */
import { authorizeActorAction, isDeviceIntent, isMaster } from './rbac.mjs';
import { needsIntelligenceLoop } from './extensusMind.mjs';

export const MODEL_ROLES = {
  classifier: 'fast intent / daemon tick',
  reasoner: 'approved writes, code, tests, complex talk',
  embed: 'local workspace search',
};

export const INTENT_ROUTING_RULES = `[INTENT_ROUTING_RULES]
- If user input is informational, conversational, or a workspace query: Execute immediately as TALK. Do not prompt for operator unlock blocks.
- If user input requests file modification, command execution, or system state change: Tag as MUTATING, verify bridge health silently, and present the standard UI APPROVE button. Never output instruction manuals or troubleshooting text unless explicitly asked.
- Talk may use read tools only. Talk must not call write tools.
- After the operator clicks APPROVE, execute. Do not explain how the gate works.`;

export const CHAT_WRITE_TOOLS = [
  'github_create_repo',
  'execute_mac_command',
  'mac_write_file',
  'teleporte_remember',
  'write_script',
  'run_tests',
  'web_save',
  'device_action',
  'enqueue_jobs',
  'mcp_bash',
  'mcp_call',
  'mcp_github',
  'mcp_cloudflare',
  'mcp_mail',
  'mcp_data',
  'edit_image',
  'edit_video',
  'write_document',
];

export const CHAT_READ_TOOLS = [
  'web_search',
  'web_fetch',
  'inspect_self',
  'mac_list_dir',
  'mac_read_file',
  'workspace_index',
  'workspace_search',
  'query_infinite_memory',
  'inspect_file',
  'read_state',
  'list_active_nodes',
  'check_node_health',
  'read_teleporte',
  'list_jobs',
];

/** Real mutations. Informational "how do I deploy" is TALK, not this. */
export const EXECUTE_RE =
  /\b(deploy|merge|commit|push|claim|release|install|restart|kill|checkin|claim_lane|release_lane|execute now|parallel go)\b/i;

const HOW_TO_RE =
  /\b(explain|what is|what's|why|how does|how do(?:es)?|how to|how can|summarize|tell me|discuss|opinion|brainstorm|analyze|architecture)\b/i;

const FILE_MUTATE_RE =
  /\b(write|save|edit|overwrite|create|delete|remove)\b[\s\S]{0,48}\b(file|script|plist|repo|directory|folder|\.md|\.js|\.mjs|\.ts|\.json|agents\.md)\b/i;

const WRITE_TO_RE = /\b(write|save|edit|append)\b[\s\S]{0,24}\b(to|into)\b/i;

const SHELL_MUTATE_RE =
  /\b(run (?:the )?(?:tests?|command|script)|execute (?:the )?(?:command|script)|shell command)\b/i;

const MEMORY_MUTATE_RE =
  /\b(post (?:to )?(?:teleporte|backroom)|remember this|write to teleporte|teleporte_remember)\b/i;

const REPO_MUTATE_RE = /\bcreate (?:a )?(?:github )?repo\b/i;

const FORCE_NOW_RE = /\b(do it|go ahead|execute now|just do it|ship it now)\b/i;

export function classifyMilitaryMemoryPosture(text) {
  const q = String(text || '').toLowerCase().trim();

  // Tier 1: Grounded Strict Canon Protection (Canon Only)
  if (
    /\b(legal|statute|law|code section|court|case law|canon|anti-hallucination|identity rule|contract spec|exact rule|policy|official doc|verified|security|auth|jwt|csrf|signature|license|liability)\b/i.test(q)
  ) {
    return {
      mode: 'strict',
      trustFilter: ['canon'],
      reason: 'Grounded Strict Canon Protection',
    };
  }

  // Tier 2: Real-time Operational & Task Execution
  if (
    /\b(deploy|build|commit|push|test|run|task|board|backroom|claim|status|health|daemon|process|log|error|fix|edit|write|file|script|repo|github)\b/i.test(q)
  ) {
    return {
      mode: 'operational',
      trustFilter: ['canon', 'operational'],
      reason: 'Live Operational & Execution Focus',
    };
  }

  // Tier 3: Multi-Source Broad Synthesis
  return {
    mode: 'broad',
    trustFilter: ['canon', 'apocrypha', 'operational'],
    reason: 'Multi-Tier Swarm Synthesis',
  };
}

export function isHowToQuery(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (FORCE_NOW_RE.test(t)) return false;
  return HOW_TO_RE.test(t);
}

export function isMutatingIntent(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (t.startsWith('/')) return true;
  return (
    EXECUTE_RE.test(t) ||
    FILE_MUTATE_RE.test(t) ||
    WRITE_TO_RE.test(t) ||
    SHELL_MUTATE_RE.test(t) ||
    MEMORY_MUTATE_RE.test(t) ||
    REPO_MUTATE_RE.test(t)
  );
}

export function isGuestLegalTalk(text) {
  const t = String(text || '');
  return (
    /Attached files:|<packet_brief>|<doc_read>|\/inbox\/\S+\.(pdf|txt|md|docx)/i.test(t)
    || /plea|admission|sentenc|lawyer|attorney|public defender|motion to withdraw|petition|warrant|76-18|rule 11|possess|distribut|discovery|bodycam/i.test(t)
  );
}

export function classifyIntent(text, actor) {
  const t = String(text || '').trim();
  if (!t) return { mode: 'idle', route: 'talk_only', execute: false, reason: 'empty' };

  if (t.startsWith('/')) {
    return { mode: 'execute', route: 'mutating', execute: true, reason: 'slash_command' };
  }

  if (isDeviceIntent(t)) {
    return { mode: 'execute', route: 'mutating', execute: true, reason: 'device_action' };
  }

  if (needsIntelligenceLoop(t)) {
    if (actor && !isMaster(actor)) {
      return { mode: 'converse', route: 'talk_only', execute: false, reason: 'guest_packet_talk' };
    }
    if (/\b(explanation|summary|wording|grammar)\b/i.test(t) && !/attached files|screenshot|<photo_read>/i.test(t)) {
      return { mode: 'converse', route: 'talk_only', execute: false, reason: 'fix_prose_not_execute' };
    }
    return { mode: 'execute', route: 'mutating', execute: true, reason: 'intelligence_loop' };
  }

  const mutating = isMutatingIntent(t);
  const howTo = isHowToQuery(t);

  if (mutating && howTo && !FORCE_NOW_RE.test(t)) {
    return { mode: 'converse', route: 'talk_only', execute: false, reason: 'how_to_not_execute' };
  }
  if (mutating) {
    if (actor && !isMaster(actor) && (isGuestLegalTalk(t) || FORCE_NOW_RE.test(t))) {
      return { mode: 'converse', route: 'talk_only', execute: false, reason: 'guest_draft_not_mac' };
    }
    return { mode: 'execute', route: 'mutating', execute: true, reason: 'mutating_query' };
  }
  return { mode: 'converse', route: 'talk_only', execute: false, reason: 'conversation' };
}

export function authorizeIntent(text, actor = { role: 'master', user_id: 'tony' }, extra = {}) {
  const intent = classifyIntent(text, actor);
  const proposed = extra.tool || extra.proposed || null;
  const target = extra.target || (isDeviceIntent(text) ? 'device' : undefined);
  const decision = authorizeActorAction(actor, intent, { tool: proposed, target });
  return {
    ...intent,
    allowed: decision.ok,
    actor: decision.actor,
    auth_mode: decision.mode,
    master: decision.master,
    needs_approve: Boolean(decision.needs_approve),
    needs_device_consent: Boolean(decision.needs_device_consent),
    sandbox: decision.sandbox,
    error: decision.error,
    hint: decision.hint,
    tool: proposed,
  };
}

export function proposeAction(text) {
  const intent = classifyIntent(text);
  if (intent.route !== 'mutating') return null;
  const t = String(text || '').trim();

  if (REPO_MUTATE_RE.test(t)) {
    const name = (t.match(/repo(?:sitory)?\s+(?:called |named )?([a-zA-Z0-9._-]+)/i) || [])[1] || '';
    return { tool: 'github_create_repo', name, summary: 'Create a GitHub repository' };
  }
  if (MEMORY_MUTATE_RE.test(t)) {
    return { tool: 'teleporte_remember', kind: 'NOTE', content: t.slice(0, 400), summary: 'Write to Teleporte Backroom' };
  }
  if (SHELL_MUTATE_RE.test(t) && /\btests?\b/i.test(t)) {
    return { tool: 'run_tests', cwd: '.', summary: 'Run local tests' };
  }
  if (FILE_MUTATE_RE.test(t) || WRITE_TO_RE.test(t)) {
    return { tool: 'write_script', summary: 'Write or edit a file on this Mac' };
  }
  if (/\b(commit|push|merge|deploy)\b/i.test(t)) {
    return { tool: 'execute_mac_command', cmd: 'git', summary: `Git / deploy: ${t.slice(0, 120)}` };
  }
  return { tool: 'execute_mac_command', summary: t.slice(0, 160) };
}
